/**
 * UnmatchedDocumentSection
 *
 * Shown in the processing view when a document's checklist matching
 * completes with zero suggestions. Allows the user to:
 * - Manually select multiple checklist items to link (multi-select with toggles)
 * - Unselect previously linked items
 * - Dismiss the unmatched state (acknowledge it)
 */

import { useState, useMemo, useRef, useEffect } from "react";
import {
  AlertCircle,
  CheckCircle2,
  CheckSquare,
  ChevronDown,
  Search,
  Square,
  X,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "convex/_generated/api";
import { motion, AnimatePresence } from "framer-motion";
import type { Id } from "@/packages/types";
import { useApiMutation } from "@/packages/features-shared";
import { useLinkedChecklistItems } from "@/hooks/use-linked-checklist-items";

interface UnmatchedDocumentSectionProps {
  documentId: Id<"documents">;
  auditId: Id<"audits">;
}

export function UnmatchedDocumentSection({
  documentId,
  auditId,
}: UnmatchedDocumentSectionProps) {
  const [isDismissed, setIsDismissed] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reactive linked items from backend
  const { items: linkedItems } = useLinkedChecklistItems(documentId);
  const linkedItemIds = useMemo(
    () => new Set(linkedItems.map((item) => item.checklistItemId)),
    [linkedItems],
  );

  // Fetch checklist items for the audit
  const { data: checklistData } = useQuery({
    ...convexQuery(api.checklist_queries.getChecklistByAuditId, { auditId }),
  });

  // Mutation to link document to checklist item
  const linkMutation = useApiMutation(
    api.checklist_mutations.linkDocumentToChecklistItem,
    {
      name: "manual-link-unmatched",
      silentSuccess: true,
    },
  );

  // Mutation to unlink document from checklist item
  const unlinkMutation = useApiMutation(
    api.checklist_mutations.unlinkDocumentFromChecklistItem,
    {
      name: "manual-unlink-unmatched",
      silentSuccess: true,
    },
  );

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (dropdownOpen) {
      const timer = setTimeout(() => searchInputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
    setSearchQuery("");
  }, [dropdownOpen]);

  // Close dropdown on click outside
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  // Build a flat list of items with category names for display
  const checklistOptions = useMemo(() => {
    if (!checklistData) return [];
    const categoryMap = new Map(
      checklistData.categories.map((c) => [c._id, c.name]),
    );
    return checklistData.items.map((item) => ({
      id: item._id as Id<"checklistItems">,
      description: item.description,
      categoryName: categoryMap.get(item.categoryId) ?? "Uncategorized",
      status: item.status,
    }));
  }, [checklistData]);

  // Filter items by search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return checklistOptions;
    const q = searchQuery.toLowerCase();
    return checklistOptions.filter(
      (item) =>
        item.description.toLowerCase().includes(q) ||
        item.categoryName.toLowerCase().includes(q),
    );
  }, [checklistOptions, searchQuery]);

  // Group by category
  const groupedOptions = useMemo(() => {
    const groups = new Map<string, typeof filteredOptions>();
    for (const item of filteredOptions) {
      const existing = groups.get(item.categoryName);
      if (existing) {
        existing.push(item);
      } else {
        groups.set(item.categoryName, [item]);
      }
    }
    return [...groups.entries()];
  }, [filteredOptions]);

  const handleToggle = (checklistItemId: Id<"checklistItems">) => {
    if (linkedItemIds.has(checklistItemId)) {
      unlinkMutation.mutate({ documentId, checklistItemId });
    } else {
      linkMutation.mutate({ documentId, checklistItemId });
    }
  };

  // Dismissed with linked items — show compact summary
  if (isDismissed && linkedItemIds.size > 0) {
    return (
      <motion.div
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20"
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />
        <p className="text-[12px] text-emerald-400">
          Linked to {linkedItemIds.size} checklist item
          {linkedItemIds.size !== 1 ? "s" : ""}
        </p>
      </motion.div>
    );
  }

  // Dismissed with no links — hide entirely
  if (isDismissed) return null;

  return (
    <motion.div
      className="space-y-2"
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      {/* Unmatched notice */}
      <div className="flex items-start gap-2">
        <AlertCircle size={14} className="text-amber-500 shrink-0 mt-0.5" />
        <p className="text-[12px] text-secondary leading-relaxed">
          No matching checklist items found. You can link this document manually
          or dismiss this notice.
        </p>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 relative">
        <div ref={containerRef} className="relative flex-1">
          <button
            type="button"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-md border border-border bg-card text-[12px] text-primary hover:bg-card-hover transition-colors"
          >
            <span className="text-muted">
              {linkedItemIds.size > 0
                ? `${linkedItemIds.size} item${linkedItemIds.size !== 1 ? "s" : ""} linked`
                : "Select checklist items..."}
            </span>
            <ChevronDown
              size={12}
              className={`text-muted transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
            />
          </button>

          {/* Dropdown */}
          <AnimatePresence>
            {dropdownOpen && (
              <motion.div
                className="absolute top-full left-0 right-0 mt-1 z-50 bg-card rounded-lg border border-border shadow-lg"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
              >
                {/* Search input */}
                <div className="flex items-center gap-2 px-2.5 py-2 border-b border-border">
                  <Search
                    className="h-3 w-3 shrink-0 text-muted"
                    strokeWidth={1.8}
                  />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search checklist items..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 bg-transparent text-[12px] text-primary placeholder:text-muted outline-none"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      className="p-0.5 rounded hover:bg-card-hover"
                    >
                      <X size={10} className="text-muted" />
                    </button>
                  )}
                </div>

                {/* Options list */}
                <div className="max-h-48 overflow-y-auto">
                  {groupedOptions.length > 0 ? (
                    groupedOptions.map(([categoryName, items]) => (
                      <div key={categoryName}>
                        <div className="px-2.5 py-1 text-[10px] uppercase text-muted font-medium tracking-wider bg-card-hover/50">
                          {categoryName}
                        </div>
                        {items.map((item) => {
                          const isLinked = linkedItemIds.has(item.id);
                          return (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => handleToggle(item.id)}
                              className="w-full text-left px-2.5 py-1.5 text-[12px] text-secondary hover:bg-card-hover transition-colors flex items-center gap-2"
                            >
                              {isLinked ? (
                                <CheckSquare
                                  size={14}
                                  className="text-accent shrink-0"
                                />
                              ) : (
                                <Square
                                  size={14}
                                  className="text-muted shrink-0"
                                />
                              )}
                              <span className="flex-1 line-clamp-2">
                                {item.description}
                              </span>
                              <span
                                className={`text-[10px] px-1 py-0.5 rounded shrink-0 ${
                                  item.status === "MISSING"
                                    ? "bg-amber-500/10 text-amber-400"
                                    : item.status === "UPLOADED"
                                      ? "bg-blue-500/10 text-blue-400"
                                      : "bg-emerald-500/10 text-emerald-400"
                                }`}
                              >
                                {item.status.toLowerCase()}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    ))
                  ) : (
                    <div className="px-2.5 py-2 text-[12px] text-muted">
                      {searchQuery.trim()
                        ? "No matching items"
                        : "No checklist items available"}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <button
          type="button"
          onClick={() => setIsDismissed(true)}
          className="px-2.5 py-1.5 rounded-md border border-border text-[12px] text-muted hover:bg-card-hover transition-colors shrink-0"
        >
          Dismiss
        </button>
      </div>
    </motion.div>
  );
}
